import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ShiftBoardEntry } from '../../services/pickupSlots.api';
import { clearGenerationTrigger, setGenerationTrigger } from '../../services/pickupSlots.api';

interface Props {
  shift: ShiftBoardEntry;
  onClose: () => void;
}

const baghdadHHmm = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Baghdad' });

/**
 * Set or reset a shift's route-generation trigger. Default is shift start −
 * T-lead; an override is PERSISTENT (applies every operational day until
 * reset). The server rejects times at/after the shift's append cutoff.
 */
export default function ShiftTriggerModal({ shift, onClose }: Props) {
  const [time, setTime] = useState(() => baghdadHHmm(shift.trigger_instant));
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const save = useMutation({
    mutationFn: () => setGenerationTrigger(shift.code, time),
    onSuccess: (data) => {
      setError(null);
      setNote(data.note);
      queryClient.invalidateQueries({ queryKey: ['pickupSlotBoard'] });
    },
    onError: (err: { response?: { data?: { error?: string } } }) => {
      setNote(null);
      setError(err.response?.data?.error ?? 'Failed to set the trigger time');
    },
  });

  const reset = useMutation({
    mutationFn: () => clearGenerationTrigger(shift.code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pickupSlotBoard'] });
      onClose();
    },
    onError: (err: { response?: { data?: { error?: string } } }) => {
      setNote(null);
      setError(err.response?.data?.error ?? 'Failed to reset the trigger time');
    },
  });

  const busy = save.isPending || reset.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <h2 className="text-lg font-semibold text-gray-900">
          {shift.code} route-generation time
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          Covers {shift.slot_codes.join(' + ')} ({shift.window.from}–{shift.window.to}). Current trigger:{' '}
          <span className="font-medium text-gray-900">{baghdadHHmm(shift.trigger_instant)} Baghdad</span>{' '}
          {shift.trigger_override ? '(custom)' : '(default: shift start − lead time)'}.
        </p>
        <p className="mt-1 text-xs text-gray-500">
          A custom time is persistent — it applies every day until you reset it. It must be before the
          shift&apos;s append cutoff. Takes effect within ~1 minute.
        </p>

        <div className="mt-4 flex items-center gap-2">
          <label htmlFor="shift-trigger-time" className="text-sm text-gray-700">
            Generate at
          </label>
          <input
            id="shift-trigger-time"
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"
          />
          <span className="text-xs text-gray-500">Baghdad time</span>
        </div>

        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        {note && <p className="mt-3 text-sm text-green-700">✓ Saved — {note}</p>}

        <div className="mt-4 flex items-center justify-between">
          <div>
            {shift.trigger_override && (
              <button
                onClick={() => reset.mutate()}
                disabled={busy}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {reset.isPending ? 'Resetting…' : 'Reset to default'}
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm">
              {note ? 'Close' : 'Cancel'}
            </button>
            <button
              onClick={() => save.mutate()}
              disabled={busy || !/^\d{2}:\d{2}$/.test(time)}
              className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {save.isPending ? 'Saving…' : 'Save time'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
